/**
 * Installer Map Store
 * Manages beacon placement locations for the installer view.
 * Follows the venueData.ts pattern for consistency.
 */

import { defineStore } from "pinia";
import { ref, computed, triggerRef } from "vue";
import type { BeaconPlacementLocation } from "@/types/api";

export const useInstallerMapStore = defineStore("installerMap", () => {
    // ================================================================
    // STATE
    // ================================================================

    const placements = ref<Map<number, BeaconPlacementLocation>>(new Map());
    const loading = ref(false);
    const error = ref<string | null>(null);
    const currentVenueId = ref<number | null>(null);

    // ================================================================
    // GETTERS - Computed Properties
    // ================================================================

    /**
     * Get all placements as an array
     */
    const allPlacements = computed<BeaconPlacementLocation[]>(() =>
        Array.from(placements.value.values()),
    );

    /**
     * Get placements that have been installed (have a beaconId)
     */

    /**
     * Get placements that are pending installation (no beaconId)
     */
    /**
     * Get placements that are pending installation (All placeholders are pending)
     */
    const pendingPlacements = computed<BeaconPlacementLocation[]>(() =>
        allPlacements.value,
    );


    /**
     * Count of pending placements
     */
    const pendingCount = computed<number>(() => pendingPlacements.value.length);

    /**
     * Total count of all placements
     */
    const totalCount = computed<number>(() => allPlacements.value.length);


    /**
     * Get placement by ID
     */
    const getPlacementById = computed(() => {
        return (id: number): BeaconPlacementLocation | undefined => {
            return placements.value.get(id);
        };
    });

    /**
     * Get placements for a specific level
     */
    const getPlacementsByLevel = computed(() => {
        return (levelId: number): BeaconPlacementLocation[] => {
            return allPlacements.value.filter((p) => p.levelId === levelId);
        };
    });

    // ================================================================
    // ACTIONS - State Mutations
    // ================================================================

    /**
     * Set placements from API response or MQTT PUT delta (replaces ALL placements)
     * Use this for:
     * - Initial API fetch
     * - Manual refresh
     * - MQTT PUT deltas (full state replacement)
     */
    function setPlacements(newPlacements: BeaconPlacementLocation[]) {
        placements.value.clear();
        newPlacements.forEach((placement) => {
            placements.value.set(placement.id, placement);
        });
        triggerRef(placements); // Force Vue to detect the change
    }

    /**
     * Add or update a single placement
     * Note: triggerRef is required because Vue doesn't track Map mutations
     */
    function upsertPlacement(placement: BeaconPlacementLocation) {
        placements.value.set(placement.id, placement);
        triggerRef(placements); // Force Vue to detect the change
    }

    /**
     * Remove a placement by ID
     * Note: triggerRef is required because Vue doesn't track Map.delete()
     */
    function removePlacement(id: number) {
        const deleted = placements.value.delete(id);
        if (deleted) {
            triggerRef(placements); // Force Vue to detect the change
        }
    }

    /**
     * Clear all placements
     */
    function clearPlacements() {
        placements.value.clear();
        triggerRef(placements); // Force Vue to detect the change
        currentVenueId.value = null;
        error.value = null;
    }

    /**
     * Set loading state
     */
    function setLoading(isLoading: boolean) {
        loading.value = isLoading;
    }

    /**
     * Set error state
     */
    function setError(errorMessage: string | null) {
        error.value = errorMessage;
    }

    /**
     * Set current venue ID
     */
    function setCurrentVenue(venueId: number) {
        currentVenueId.value = venueId;
    }

    // ================================================================
    // METHOD-BASED MUTATIONS (MQTT Delta Support)
    // All mutations use triggerRef to ensure Vue detects Map changes
    // ================================================================

    /**
     * PATCH: Merge partial data with existing placement
     */
    function mergePlacement(
        item: Partial<BeaconPlacementLocation> & { id: number },
    ) {
        const existing = placements.value.get(item.id);
        if (existing) {
            placements.value.set(item.id, { ...existing, ...item });
        } else {
            placements.value.set(item.id, item as BeaconPlacementLocation);
        }
        triggerRef(placements);
    }

    /**
     * PATCH: Replace individual placement by ID (incremental update)
     * Note: For MQTT PUT (full replacement), use setPlacements() instead
     */
    function replacePlacement(
        item: Partial<BeaconPlacementLocation> & { id: number },
    ) {
        placements.value.set(item.id, item as BeaconPlacementLocation);
        triggerRef(placements);
    }

    /**
     * POST: Add new placement (or replace if exists)
     */
    function addPlacement(
        item: Partial<BeaconPlacementLocation> & { id: number },
    ) {
        placements.value.set(item.id, item as BeaconPlacementLocation);
        triggerRef(placements);
    }

    /**
     * Apply placement update based on HTTP method (incremental operations only)
     * Note: PUT (full replacement) should use setPlacements() instead
     *
     * MQTT Delta Handling:
     * - POST: Add new placement to existing state
     * - PATCH: Merge/update existing placement
     * - DELETE: Remove placement from existing state
     * - PUT: Should NOT use this - use setPlacements() for full replacement
     */
    function applyPlacementByMethod(
        item: Partial<BeaconPlacementLocation> & { id: number },
        method?: string,
    ) {
        const normalizedMethod = method?.toUpperCase();

        switch (normalizedMethod) {
            case "PATCH":
            case "UPDATE":
                mergePlacement(item);
                break;
            case "POST":
            case "CREATE":
                addPlacement(item);
                break;
            case "DELETE":
                removePlacement(item.id);
                break;
            case "PUT":
                // PUT should use setPlacements() for full replacement
                // But if called here, treat as replace individual (for backward compatibility)
                console.warn(
                    "[InstallerMapStore] PUT on individual item - should use setPlacements() for full replacement",
                );
                replacePlacement(item);
                break;
            default:
                // Default to merge for backward compatibility
                mergePlacement(item);
        }
    }

    // ================================================================
    // MQTT HANDLERS (Legacy - prefer applyPlacementByMethod)
    // ================================================================

    /**
     * Handle beacon placement update from MQTT stream.
     * Called when a placement is created, updated, or a beacon is assigned.
     * @deprecated Use applyPlacementByMethod with proper method parameter
     */
    function handlePlacementUpdate(placement: BeaconPlacementLocation) {
        upsertPlacement(placement);
    }

    /**
     * Handle beacon placement deletion from MQTT stream.
     * @deprecated Use applyPlacementByMethod with method="DELETE"
     */
    function handlePlacementDelete(id: number) {
        removePlacement(id);
    }

    // ================================================================
    // RETURN PUBLIC API
    // ================================================================

    return {
        // State
        placements,
        loading,
        error,
        currentVenueId,

        // Computed
        allPlacements,
        pendingPlacements,
        pendingCount,
        totalCount,
        getPlacementById,
        getPlacementsByLevel,

        // Actions
        setPlacements,
        upsertPlacement,
        removePlacement,
        clearPlacements,
        setLoading,
        setError,
        setCurrentVenue,

        // Method-based mutations (MQTT Delta Support)
        mergePlacement,
        replacePlacement,
        addPlacement,
        applyPlacementByMethod,

        // MQTT Handlers (Legacy)
        handlePlacementUpdate,
        handlePlacementDelete,
    };
});
